import React, { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
    validateFirstname,
    ContactTypes,
    validateLastname,
    validateContactEmail,
    validateMobileNumber,
} from '../../../Validation';
import some from 'lodash/some';
import { SectionProps } from '../../types';
import Panel from '@sharedComponents/Panel';
import InputText from "@sharedComponents/InputText";

const ProductionManagerForm = (props: SectionProps) => {
    const {
        stepValues,
        setStepValues,
        onChangeValues,
        formErrors,
        setFormErrors,
    } = props
    const { t } = useTranslation()

    const onChangeFirstname = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            productManager: {
                ...stepValues.contactInfo?.productManager,
                firstname: value,
            },
        }

        setStepValues({ ...stepValues, contactInfo: obj })
        onChangeValues && onChangeValues({ contactInfo: obj })

        const errors = validateFirstname(
            value,
            formErrors,
            ContactTypes.ProductManager
        )
        setFormErrors(errors)
    }

    const onChangeLastname = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            productManager: {
                ...stepValues.contactInfo?.productManager,
                lastname: value,
            },
        }

        setStepValues({ ...stepValues, contactInfo: obj })
        onChangeValues && onChangeValues({ contactInfo: obj })

        const errors = validateLastname(
            value,
            formErrors,
            ContactTypes.ProductManager
        )
        setFormErrors(errors)
    }

    const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            productManager: {
                ...stepValues.contactInfo?.productManager,
                email: value,
            },
        }

        setStepValues({ ...stepValues, contactInfo: obj })
        props.onChangeValues && props.onChangeValues({ contactInfo: obj })

        const errors = validateContactEmail(
            value,
            formErrors,
            ContactTypes.ProductManager
        )
        setFormErrors(errors)
    }

    const onChangeMobile = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            productManager: {
                ...stepValues.contactInfo?.productManager,
                mobileNumber: value,
            },
        }

        setStepValues({ ...stepValues, contactInfo: obj })
        props.onChangeValues && props.onChangeValues({ contactInfo: obj })

        const errors = validateMobileNumber(
            value,
            formErrors,
            ContactTypes.ProductManager
        )
        setFormErrors(errors)
    }

    const onChangePhone = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            productManager: {
                ...stepValues.contactInfo?.productManager,
                phoneNumber: value,
            },
        }
        setStepValues({ ...stepValues, contactInfo: obj })
        props.onChangeValues && props.onChangeValues({ contactInfo: obj })
    }

    const uniqueErrors = (key: string) => {
        const errors = formErrors.find(err => err.key === key);
        const uniqueErr = errors ? [...new Set(errors.messages)] : [];

        return uniqueErr.length > 0 ? uniqueErr.join(', ') : '';
    }

    return (
        <div className='producer-form producer-panel-wrapper'>
            <Panel className="panel--crb">
                <h3>{t('companyRegistration.productManager')}</h3>
                <div className='producer-form__fields'>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-pm-firstname"
                            label={t('global.firstname')}
                            required={true}
                            hasError={
                                some(formErrors, [
                                    'key',
                                    `${ContactTypes.ProductManager}Firstname`,
                                ])
                            }
                            value={
                                stepValues.contactInfo?.productManager
                                    ?.firstname || ''
                            }
                            maxLength={50}
                            onChange={onChangeFirstname}
                            autoComplete="given-name"
                            errorText={uniqueErrors(`${ContactTypes.ProductManager}Firstname`)}
                        />
                    </div>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-pm-lastname"
                            label={t('global.lastname')}
                            required={true}
                            hasError={
                                some(formErrors, [
                                    'key',
                                    `${ContactTypes.ProductManager}Lastname`,
                                ])
                            }
                            value={
                                stepValues.contactInfo?.productManager
                                    ?.lastname || ''
                            }
                            maxLength={50}
                            onChange={onChangeLastname}
                            autoComplete="family-name"
                            errorText={uniqueErrors(`${ContactTypes.ProductManager}Lastname`)}
                        />
                    </div>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-pm-email"
                            label={t('global.email')}
                            required={true}
                            hasError={
                                some(formErrors, [
                                    'key',
                                    `${ContactTypes.ProductManager}Email`,
                                ])
                            }
                            value={
                                stepValues.contactInfo?.productManager?.email ||
                                ''
                            }
                            onChange={onChangeEmail}
                            type="email"
                            autoComplete="email"
                            maxLength={250}
                            errorText={uniqueErrors(`${ContactTypes.ProductManager}Email`)}
                        />
                    </div>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-pm-mobile"
                            label={t('global.mobileNumber')}
                            required={true}
                            hasError={
                                some(formErrors, [
                                    'key',
                                    `${ContactTypes.ProductManager}MobileNumber`,
                                ])
                            }
                            value={
                                stepValues.contactInfo?.productManager
                                    ?.mobileNumber || ''
                            }
                            maxLength={30}
                            onChange={onChangeMobile}
                            autoComplete="tel"
                            errorText={uniqueErrors(`${ContactTypes.ProductManager}MobileNumber`)}
                        />
                    </div>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-pm-phone"
                            label={t('global.phoneNumber')}
                            value={
                                stepValues.contactInfo?.productManager
                                    ?.phoneNumber || ''
                            }
                            maxLength={30}
                            onChange={onChangePhone}
                            autoComplete="tel"
                        />
                    </div>
                </div>
            </Panel>
        </div>
    )
}

export default ProductionManagerForm;